/**
 * Merchant video — timing.
 *
 * Same beat grid as the B2C video (`b2c/timing.ts`): `song.mp3` measured at
 * 129.33 BPM, first beat 29 ms in, so a beat is 13.92 frames, not 14 (14
 * put the spot's last cut ~60 ms behind the music). Cut frames are rounded
 * from the cumulative beat time, so every hard cut stays within half a
 * frame of its beat. Two cuts share the scenes:
 *
 *   spot    ~15.4s the full commercial (Reels / TikTok / feed / brand page)
 *   bumper  ~6s    unskippable-length teaser, newsletter GIF, story loop
 *
 * Scenes read their own length from `useVideoConfig().durationInFrames`
 * inside their `<Sequence>`, so a scene can hold longer in one cut than in
 * the other without a second implementation. Change pacing here only.
 */

export const MERCHANT_FPS = 30;
/** Measured tempo of `song.mp3` and the time of its first beat. */
const BPM = 129.33;
const FIRST_BEAT_S = 0.029;
/** Frames per beat (fractional, ≈ 13.92). Round anything placed with it. */
export const BEAT = (MERCHANT_FPS * 60) / BPM;
/** Video frame of a beat index, on the song's grid (the video starts on beat 0). */
const beatFrame = (beats: number) =>
  beats === 0 ? 0 : Math.round(MERCHANT_FPS * FIRST_BEAT_S + beats * BEAT);

export type SceneKey = "hook" | "showcase" | "offer" | "payoff" | "end";
export type Cut = "spot" | "bumper";

/** `first`: the scene opens the video, so its frame 0 is the thumbnail. */
export type SceneProps = { first: boolean };

/**
 * Scene lengths in beats, in playback order. Hold rule (same as B2C):
 * snappy motion, but a landed line holds ≥ ~1.5s + 0.3s per word. Holds
 * are reading time: never shorten one to tighten pacing; reveal the line
 * earlier, cut words, or add a beat here.
 */
export const CUTS: Record<Cut, [SceneKey, number][]> = {
  spot: [
    ["hook", 6], //      product photo, "what brand is that??" → the brand, as a link
    ["showcase", 7], //  logo + the brand in one line + 3 photos dealt on the beat
    ["offer", 7], //     brand colour: recommend {name} → up to {reward} per order
    ["payoff", 6], //    a sale through your link lands → real cash, not points
    ["end", 7], //       logo × Frak, "get your link on {domain}", stores
  ],
  bumper: [
    ["showcase", 4],
    ["offer", 5],
    ["end", 4],
  ],
};

export type Timeline = {
  scenes: { key: SceneKey; from: number; frames: number }[];
  duration: number;
};

export const timelineFor = (cut: Cut): Timeline => {
  let beats = 0;
  const scenes = CUTS[cut].map(([key, length]) => {
    const from = beatFrame(beats);
    beats += length;
    return { key, from, frames: beatFrame(beats) - from };
  });
  return { scenes, duration: beatFrame(beats) };
};
